import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { interviewFeatures } from '../data/mock';
import { Video, Calendar, Users, Mic, Code, Play, Settings, Check, Terminal, Database, GitBranch, ClipboardList, Star, MessageSquare, BarChart } from 'lucide-react';

const InterviewsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Stage</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              Where great
              <br />
              <span className="text-neutral-400">interviews happen.</span>
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              Not another video call. A complete interview experience. 
              Collaborative coding. Real-time evaluation. Zero awkwardness.
            </p>
          </div>
        </div>
      </section>

      {/* Interview Features */}
      <section className="section bg-black relative">
        <div className="absolute inset-0 grid-bg-dark" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 mb-16">
            <div className="bg-neutral-900 border border-neutral-800 p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-white text-xl font-semibold">Orchestration</h3>
              </div>
              <p className="text-neutral-400 mb-6">Schedule, coordinate, and execute with military precision.</p>
              <ul className="space-y-4">
                {interviewFeatures.management.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="w-5 h-5 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                    <span className="text-neutral-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Video className="w-5 h-5 text-black" />
                </div>
                <h3 className="text-white text-xl font-semibold">The Live Experience</h3>
              </div>
              <p className="text-neutral-400 mb-6">Interview rooms that feel like being in the same room. Almost.</p>
              <ul className="space-y-4">
                {interviewFeatures.live.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="w-5 h-5 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                    <span className="text-neutral-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Interview Room Preview */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
            <div className="bg-neutral-800 px-6 py-4 flex items-center justify-between border-b border-neutral-700">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-neutral-600 rounded-full" />
                  <div className="w-3 h-3 bg-neutral-600 rounded-full" />
                  <div className="w-3 h-3 bg-neutral-600 rounded-full" />
                </div>
                <span className="text-white font-medium">Sandgen Stage</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-white text-sm font-medium">00:32:15</span>
                <button className="p-2 bg-neutral-700 rounded-lg hover:bg-neutral-600 transition-colors">
                  <Settings className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
            <div className="grid lg:grid-cols-2">
              <div className="p-8 border-r border-neutral-800">
                <div className="aspect-video bg-neutral-800 rounded-lg flex items-center justify-center mb-6">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="w-10 h-10 text-neutral-400" />
                    </div>
                    <p className="text-neutral-500 text-sm">HD Video Feed</p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <button className="p-4 bg-neutral-800 rounded-full hover:bg-neutral-700 transition-colors">
                    <Mic className="w-5 h-5 text-white" />
                  </button>
                  <button className="p-4 bg-neutral-800 rounded-full hover:bg-neutral-700 transition-colors">
                    <Video className="w-5 h-5 text-white" />
                  </button>
                  <button className="px-6 py-4 bg-neutral-700 rounded-full hover:bg-neutral-600 transition-colors">
                    <span className="text-white text-sm font-medium">End Session</span>
                  </button>
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Code className="w-4 h-4 text-neutral-400" />
                  <span className="text-white text-sm font-medium">Sandgen Code</span>
                  <span className="ml-auto px-2 py-1 bg-neutral-700 text-neutral-300 text-xs rounded">JavaScript</span>
                </div>
                <div className="bg-neutral-950 rounded-lg p-4 font-mono text-sm h-48 overflow-hidden">
                  <div className="text-neutral-500">// Implement a function to reverse a string</div>
                  <div className="text-neutral-300 mt-2">function <span className="text-white">reverseString</span>(str) {'{'}</div>
                  <div className="text-neutral-300 ml-4">return str.split(&apos;&apos;).reverse().join(&apos;&apos;);</div>
                  <div className="text-neutral-300">{'}'}</div>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg font-medium text-sm hover:bg-neutral-200 transition-colors">
                    <Play className="w-4 h-4" />
                    Execute
                  </button>
                  <span className="text-neutral-500 text-sm">Output: &quot;olleh&quot;</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sandgen Code */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Code</span>
              <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
                Collaborative coding <span className="text-neutral-400">that actually works.</span>
              </h2>
              <p className="text-lg text-neutral-500 mb-10">
                Real-time. Multi-language. Zero lag. The technical interview experience 
                your engineers have been waiting for.
              </p>
              <div className="space-y-5">
                {[
                  { icon: Code, text: '20+ languages. From Python to Rust.' },
                  { icon: Terminal, text: 'Instant execution. Real-time results.' },
                  { icon: Database, text: 'Full history. Every keystroke preserved.' },
                  { icon: GitBranch, text: 'Version tracking. Time travel included.' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <span className="w-10 h-10 bg-white border border-neutral-200 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-neutral-600" />
                    </span>
                    <span className="text-neutral-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-xl">
              <div className="bg-neutral-900 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-neutral-700 rounded-full" />
                  <div className="w-3 h-3 bg-neutral-700 rounded-full" />
                  <div className="w-3 h-3 bg-neutral-700 rounded-full" />
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-neutral-400 text-sm">Python</span>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white text-xs">Synced</span>
                  </div>
                </div>
              </div>
              <div className="p-6 font-mono text-sm bg-neutral-950 text-neutral-300">
                <div><span className="text-neutral-500">def</span> <span className="text-white">two_sum</span>(nums, target):</div>
                <div className="text-neutral-600 ml-4">&quot;&quot;&quot;The classic. Elegantly solved.&quot;&quot;&quot;</div>
                <div className="ml-4">seen = {'{}'}</div>
                <div className="ml-4"><span className="text-neutral-500">for</span> i, num <span className="text-neutral-500">in</span> enumerate(nums):</div>
                <div className="ml-8">complement = target - num</div>
                <div className="ml-8"><span className="text-neutral-500">if</span> complement <span className="text-neutral-500">in</span> seen:</div>
                <div className="ml-12"><span className="text-neutral-500">return</span> [seen[complement], i]</div>
                <div className="ml-8">seen[num] = i</div>
                <div className="ml-4"><span className="text-neutral-500">return</span> []</div>
              </div>
              <div className="border-t border-neutral-200 p-5 bg-neutral-50">
                <div className="bg-white border border-neutral-200 p-3 rounded-lg font-mono text-sm">
                  <div className="text-neutral-700 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    <span>All tests passing. Impressive.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sandgen Score */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Score</span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              Evaluation. <span className="text-neutral-400">Elevated.</span>
            </h2>
            <p className="text-lg text-neutral-500">
              AI-assisted scoring meets human intuition. The complete picture, every time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: ClipboardList, title: 'Rubric Precision', desc: 'Custom criteria. Weighted scores. Bias eliminated.' },
              { icon: Star, title: 'Neural Scoring', desc: 'AI analysis of JD alignment and response quality.' },
              { icon: MessageSquare, title: 'Complete Record', desc: 'Full recordings. Auto-transcripts. Nothing missed.' }
            ].map((item, index) => (
              <div key={index} className="bg-neutral-50 border border-neutral-100 p-10 group hover:bg-white hover:border-neutral-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-white border border-neutral-200 rounded-xl flex items-center justify-center mb-8 group-hover:bg-black group-hover:border-black group-hover:text-white transition-all duration-300">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-black text-lg font-semibold mb-3">{item.title}</h3>
                <p className="text-neutral-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Evaluation Preview */}
          <div className="bg-neutral-50 border border-neutral-100 rounded-xl overflow-hidden">
            <div className="bg-white px-8 py-5 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <h4 className="text-black font-medium">Candidate Scorecard</h4>
                <p className="text-neutral-500 text-sm">Sarah Chen — Senior Software Engineer</p>
              </div>
              <span className="px-4 py-2 bg-black text-white font-semibold rounded-full text-sm">92/100</span>
            </div>
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <h5 className="text-black font-medium mb-6">Performance Metrics</h5>
                  <div className="space-y-5">
                    {[
                      { name: 'Technical Mastery', score: 95 },
                      { name: 'Problem Solving', score: 90 },
                      { name: 'Communication', score: 88 },
                      { name: 'Culture Alignment', score: 94 }
                    ].map((item) => (
                      <div key={item.name}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-neutral-700 text-sm">{item.name}</span>
                          <span className="text-black font-medium text-sm">{item.score}%</span>
                        </div>
                        <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div className="h-full bg-black rounded-full" style={{ width: `${item.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h5 className="text-black font-medium mb-6">Neural Analysis</h5>
                  <div className="bg-white border border-neutral-200 p-6 rounded-xl">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart className="w-4 h-4 text-neutral-600" />
                      <span className="text-black text-sm font-medium">Exceptional Match</span>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2"><span className="text-black font-bold">+</span><span className="text-neutral-600">Distributed systems expertise confirmed</span></li>
                      <li className="flex items-start gap-2"><span className="text-black font-bold">+</span><span className="text-neutral-600">Articulate and structured responses</span></li>
                      <li className="flex items-start gap-2"><span className="text-black font-bold">+</span><span className="text-neutral-600">Strong algorithmic thinking</span></li>
                      <li className="flex items-start gap-2"><span className="text-neutral-400">~</span><span className="text-neutral-400">Frontend depth: growth opportunity</span></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InterviewsPage;
